import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Box,
  Chip,
} from '@mui/material';
import MediaDisplay from '../Media/MediaDisplay';

const QuestionCard = ({ question, questionNumber, totalQuestions, answer, onAnswerChange }) => {
  const handleSingleChoiceChange = (event) => {
    onAnswerChange(event.target.value);
  };

  const handleMultipleChoiceChange = (optionText) => {
    const currentAnswers = Array.isArray(answer) ? answer : [];
    let newAnswers;
    
    if (currentAnswers.includes(optionText)) {
      newAnswers = currentAnswers.filter(a => a !== optionText);
    } else {
      newAnswers = [...currentAnswers, optionText];
    }
    
    onAnswerChange(newAnswers);
  };

  const handleOpenTextChange = (event) => {
    onAnswerChange(event.target.value);
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header avec numéro de question */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Chip 
            label={`Question ${questionNumber}/${totalQuestions}`} 
            color="primary" 
            variant="outlined"
          />
          <Chip 
            label={`${question.points || 1} point${(question.points || 1) > 1 ? 's' : ''}`} 
            color="secondary"
            size="small"
          />
        </Box>

        {/* Texte de la question */}
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {question.question}
        </Typography>

        {/* Description optionnelle */}
        {question.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {question.description}
          </Typography>
        )}

        {/* Média si présent */}
        <MediaDisplay media={question.media} />

        {/* Options selon le type de question */}
        {question.type === 'single-choice' && (
          <RadioGroup value={answer || ''} onChange={handleSingleChoiceChange}>
            {question.options.map((option, index) => (
              <FormControlLabel
                key={option.id || index}
                value={option.text}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={String.fromCharCode(65 + index)} 
                      size="small" 
                      variant="outlined"
                      sx={{ minWidth: 32 }}
                    />
                    <Typography>{option.text}</Typography>
                  </Box>
                }
                sx={{ 
                  mb: 1, 
                  p: 1.5, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: answer === option.text ? 'primary.main' : 'divider',
                  bgcolor: answer === option.text ? 'primary.lighter' : 'transparent',
                  transition: 'all 0.2s'
                }}
              />
            ))}
          </RadioGroup>
        )}

        {question.type === 'multiple-choice' && (
          <FormGroup>
            {question.options.map((option, index) => {
              const currentAnswers = Array.isArray(answer) ? answer : [];
              const isChecked = currentAnswers.includes(option.text);
              
              return (
                <FormControlLabel
                  key={option.id || index}
                  control={
                    <Checkbox 
                      checked={isChecked}
                      onChange={() => handleMultipleChoiceChange(option.text)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={String.fromCharCode(65 + index)} 
                        size="small" 
                        variant="outlined"
                        sx={{ minWidth: 32 }}
                      />
                      <Typography>{option.text}</Typography>
                    </Box>
                  }
                  sx={{ 
                    mb: 1, 
                    p: 1.5, 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isChecked ? 'primary.main' : 'divider',
                    bgcolor: isChecked ? 'primary.lighter' : 'transparent',
                    transition: 'all 0.2s'
                  }}
                />
              );
            })}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Sélectionnez toutes les bonnes réponses
            </Typography>
          </FormGroup>
        )}

        {question.type === 'true-false' && (
          <RadioGroup value={answer || ''} onChange={handleSingleChoiceChange}>
            {question.options.map((option) => (
              <FormControlLabel
                key={option.id}
                value={option.text}
                control={<Radio />}
                label={option.text}
                sx={{ 
                  mb: 1, 
                  p: 1.5, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: answer === option.text ? 'primary.main' : 'divider',
                  bgcolor: answer === option.text ? 'primary.lighter' : 'transparent',
                  transition: 'all 0.2s'
                }}
              />
            ))}
          </RadioGroup>
        )}

        {question.type === 'open-text' && (
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Saisissez votre réponse ici..."
            value={answer || ''}
            onChange={handleOpenTextChange}
            variant="outlined"
          />
        )}
      </CardContent>
    </Card>
  );
};

export default QuestionCard;

